import React, { useState } from 'react';
import { message, Modal } from 'antd';
import moment from 'moment';
import { makeGetCall } from '../../../../utils/helper';
import PdfViewer from '../../../../components/PdfViewer';
import { ALLOWED_FILE_TYPES } from '../../constants';
import type { PatientNote, PatientNoteGroup, Patient, PacsOrder } from '../../types';

interface PatientNotesProps {
  notes: PatientNoteGroup[];
  patient?: Patient;
  pacsOrder?: PacsOrder;
}

const PatientNotes: React.FC<PatientNotesProps> = ({ notes, patient, pacsOrder }) => {
  const [selectedNote, setSelectedNote] = useState<PatientNote | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [documentData, setDocumentData] = useState<ArrayBuffer | null>(null);
  const [documentMimeType, setDocumentMimeType] = useState('');

  const handleDocumentClick = async (note: PatientNote) => {
    setSelectedNote(note);
    setModalVisible(true);

    if (note.rn_upload_path && note.rn_upload_type !== 'notes') {
      try {
        const ext = note.rn_upload_path.split('.').pop() || '';
        setDocumentMimeType(ALLOWED_FILE_TYPES[ext] || '');
        const response = await makeGetCall(`/fetch-note-file-by-id?note_id=${note.rn_id}`, { responseType: 'arraybuffer' });
        if (response?.data) setDocumentData(response.data);
      } catch {
        console.error('Error loading document');
        message.error('Failed to load document');
        setDocumentData(null);
      }
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setSelectedNote(null);
    setDocumentData(null);
  };

  if (!notes || !notes.length) {
    return <div className="no-content-message">No documents available</div>;
  }

  return (
    <>
      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {notes.map(dateNote => (
          <div key={dateNote.creationdate}>
            <div style={{ fontWeight: 500, fontSize: '0.875rem', color: '#666', padding: '0.15rem 0' }}>
              {dateNote.creationdate}
            </div>
            {dateNote.rows?.map(note => {
              const type = note.rn_upload_type?.toUpperCase() || '';
              const name = note.rn_file_name ? ` | ${note.rn_file_name}` : '';
              const date = moment(note.createdAt).format('DD/MM/YYYY | HH:mm:ss') || '';
              const displayText = `${type}${name} | ${date}`;
              return (
                <div
                  key={note.rn_id}
                  onClick={() => handleDocumentClick(note)}
                  className={`report-notes-list ${note.rn_id === selectedNote?.rn_id ? 'selected' : ''}`}
                  title={displayText}
                >
                  {displayText}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {modalVisible && (
        <Modal
          title={selectedNote?.rn_file_name || selectedNote?.rn_upload_type?.toUpperCase()}
          width={1000}
          style={{ top: '5%' }}
          open={modalVisible}
          onCancel={closeModal}
          okButtonProps={{ style: { display: 'none' } }}
          cancelButtonProps={{ style: { display: 'none' } }}
        >
          {selectedNote?.rn_upload_type === 'notes' ? (
            <div style={{ padding: '0.5rem 1rem', maxHeight: '600px', overflowY: 'auto', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              {selectedNote.rn_notes}
            </div>
          ) : (
            <div style={{ height: '80vh', overflowY: 'auto' }}>
              <PdfViewer
                pdfArrayBuffer={documentData}
                patient_id={patient?.pat_id}
                order_id={pacsOrder?.pacs_ord_id}
                patientData={{ patient, ps_study_dt_tm: selectedNote?.createdAt }}
                mimeType={documentMimeType}
                hideCopyButton
                hideWhatsappButton
              />
            </div>
          )}
        </Modal>
      )}
    </>
  );
};

export default PatientNotes;
